// filepath: apps/web/src/design-system/Dialog.tsx
//
// FR-UI-CORE-9 / FR-UI-OPS-3 — Dialog primitive from the design system
// inventory (`docs/guides/design system/components/feedback/Dialog.jsx`).
//
// Ported from the reference JSX to TypeScript for the production
// React SPA. Renders as `role="dialog"` + `aria-modal="true"` with a
// backdrop click-to-close affordance.
//
// IMPORTANT: this Dialog is a *visual* primitive. Caller is responsible
// for focus trap behavior (per FR-UI-CORE-9). The operator drill-down
// composes its own focus trap on top of this primitive.

import React from "react";

export interface DialogProps {
  /** When false, the component renders nothing. */
  open: boolean;
  title?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  /** Footer action buttons, e.g. cancel + confirm. */
  actions?: React.ReactNode;
  /** Optional accessible label id; if absent, derived from `title` via aria-labelledby. */
  "aria-labelledby"?: string;
  /** Test hook forwarded onto the dialog container. */
  "data-testid"?: string;
  /** Extra className passthrough. */
  className?: string;
  /** Inline style passthrough (token-only). */
  style?: React.CSSProperties;
}

/**
 * Dialog — modal overlay primitive. Backdrop click closes (unless
 * the caller suppresses the `onClose` prop). The click on the inner
 * panel does NOT close (event is stopped).
 */
export function Dialog({
  open,
  title,
  children,
  onClose,
  actions,
  className,
  style,
  "aria-labelledby": ariaLabelledBy,
  ...rest
}: DialogProps): React.ReactElement | null {
  if (!open) return null;
  const titleId = "ds-dialog-title";
  return (
    <div
      onClick={() => onClose?.()}
      className={["ds-dialog__backdrop", className].filter(Boolean).join(" ")}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--surface-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        ...style,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy ?? (title ? titleId : undefined)}
        className="ds-dialog__panel"
        {...rest}
        style={{
          background: "var(--surface-card)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          width: "420px",
          maxWidth: "90vw",
          padding: "var(--space-6)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {title ? (
          <h2
            id={ariaLabelledBy ?? titleId}
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--text-primary)",
              margin: "0 0 12px",
            }}
          >
            {title}
          </h2>
        ) : null}
        <div
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--text-body)",
            lineHeight: "var(--leading-relaxed)",
          }}
        >
          {children}
        </div>
        {actions ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "20px",
            }}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
